import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';
import { Mutex } from 'async-mutex';
import { KeyValueStore } from './KeyValueStore';

export class KeyValueStoreNative implements KeyValueStore {
    private readonly TRPLIT_DB_NAME = 'triplit-db';

    // We must enforce serialization of access to the database unfortunately,
    // as the underlying SQLite engine does not support concurrent reads or writes.
    private mutex = new Mutex();
    private sqlite: SQLiteConnection = new SQLiteConnection(CapacitorSQLite);
    private conn: SQLiteDBConnection;

    constructor() {}

    async init(): Promise<void> {
        this.conn = await this.setupConnection();
        console.info('Database connection established.');
    }

    async dispose(): Promise<void> {
        await this.sqlite.closeConnection(this.TRPLIT_DB_NAME, /* readonly= */ false);
    }

    async getValue(key: string): Promise<string | null> {
        return this.mutex.runExclusive(async () => {
            const result = await this.conn.query('SELECT value FROM kv_store WHERE key = ?', [key]);
            // console.log(`getValue(${key})`, result);
            return (result?.values?.[0]?.value ?? null) as string | null;
        });
    }

    async getItems(keys: string[]): Promise<Map<string, string>> {
        return this.mutex.runExclusive(async () => {
            const placeholders = keys.map(() => '?').join(',');
            const rows = await this.conn.query(`SELECT key, value FROM kv_store WHERE key IN (${placeholders})`, keys);
            const pairs: [string, string][] = rows.values?.map((value) => [value.key, value.value]) ?? [];
            return new Map(pairs);
        });
    }

    async getAllKeys(): Promise<string[]> {
        return this.mutex.runExclusive(async () => {
            const result = await this.conn.query('SELECT key FROM kv_store');
            return (result?.values?.map((value) => value.key) ?? []) as string[];
        });
    }

    async setValue(key: string, value: string): Promise<void> {
        return this.mutex.runExclusive(async () => {
            try {
                await this.conn.run('INSERT OR REPLACE INTO kv_store (key, value) VALUES (?, ?)', [key, value]);
            } catch (e) {
                console.error(`Failed to set value for key "${key}":`, e);
                throw e;
            }
        });
    }

    async removeItem(key: string): Promise<void> {
        return this.mutex.runExclusive(async () => {
            try {
                await this.conn.run('DELETE FROM kv_store WHERE key = ?', [key]);
            } catch (e) {
                console.error(`Failed to remove item with key "${key}":`, e);
                throw e;
            }
        });
    }

    async isHealthy(): Promise<boolean> {
        return this.mutex.runExclusive(async () => {
            try {
                await this.conn.query('SELECT 1');
                return true;
            } catch (e) {
                console.error('Connection check failed:', e);
                return false;
            }
        });
    }

    private async setupConnection(): Promise<SQLiteDBConnection> {
        console.info('Setting up connection to the database...');
        const connection: SQLiteDBConnection = await this.createAndOpenConnection();
        (window as any).dumpKVStore = async () => {
            const rows = await connection.query('SELECT key, value FROM kv_store');
            if (!rows || !rows.values || rows.values.length === 0) {
                console.log('No rows found in kv_store');
                return;
            }
            console.log('Dumping kv_store contents:');
            console.log(rows);
            console.table(rows.values);
        };
        return connection;
    }

    private async createAndOpenConnection() {
        // Note: we do this aggressively, without checking whether a connection exists,
        // because on native platforms we cannot accurately detect if a connection exists
        // on the native side, and if it does we will get an error when trying to create a new one.
        console.info('Closing any pre-existing connection to the database...');
        try {
            await this.sqlite.closeConnection(this.TRPLIT_DB_NAME, /* readonly= */ false);
        } catch (e) {
            console.warn('Failed to close pre-existing connection, it may not exist:', e);
        }
        console.info('Creating a new connection to the database...');
        const conn = await this.sqlite.createConnection(
            this.TRPLIT_DB_NAME,
            /* encrypted= */ false,
            'no-encryption',
            1,
            /* readonly= */ false
        );
        console.info('Opening connection to the database...');
        await conn.open();
        console.info('Creating kv_store table in the database if it does not exist...');
        await conn.execute(`
            CREATE TABLE IF NOT EXISTS kv_store (
                key TEXT PRIMARY KEY,
                value TEXT
            );`);
        return conn;
    }

    /** Completely clear the store. */
    async destroy(): Promise<void> {
        console.warn('Destroying the key-value store. This will delete all data!');
        return this.mutex.runExclusive(() => {
            return CapacitorSQLite.deleteDatabase({ database: this.TRPLIT_DB_NAME, readonly: false });
        });
    }
}
